import type { ClaySnapshot, Tool } from "./clay.ts";
import { transact } from "./store.ts";
import type { Frame } from "./timelapse.ts";

/**
 * The piece being kneaded, kept in the browser step by step so that reloading the page
 * picks it up where it was: the request, the clay with the steps it can be undone to, the
 * timelapse so far, and how the tools were set.
 *
 * Keys in the store: "meta" (WorkMeta), ["step", n] (WorkStep) and ["frame", n] (Frame),
 * n counting the kneading steps in effect (see main.ts, recordStep).
 */

export interface WorkMeta {
  /** The request's id. */
  request: string;
  symmetric: boolean;
  wheel: boolean;
  tool: Tool;
  brush: number;
  /** Left at the title screen: a reload goes back there, which offers to go on with it. */
  paused?: boolean;
}

/** A kneading step kept: the clay after it, and the strokes counted by then. */
export interface WorkStep {
  step: number;
  strokes: number;
  clay: ClaySnapshot;
}

export interface Work extends WorkMeta {
  /** The steps that can still be undone to, oldest first; the last is the clay as it is. */
  steps: WorkStep[];
  /** The timelapse's frames, oldest first. */
  frames: Frame[];
}

const STORE = "work";
/** Keys of one kind with n in [from, to). */
const keys = (kind: "step" | "frame", from = -Infinity, to = Infinity) =>
  IDBKeyRange.bound([kind, from], [kind, to], false, true);

let queue = Promise.resolve();

/** Writes one after another, in the order asked; a write that fails (no storage) is dropped. */
function write(use: (s: IDBObjectStore) => void): void {
  queue = queue.then(() => transact(STORE, "readwrite", use)).catch(() => {});
}

/**
 * Keeps the step just reached. Steps from it on (undone ones) are replaced, steps before
 * `oldest` (beyond undoing) dropped, and frames the timelapse no longer has (`frames`, the
 * steps of those it has) dropped.
 */
export function saveStep(
  meta: WorkMeta,
  step: WorkStep,
  frame: Frame,
  frames: number[],
  oldest: number,
): void {
  write((s) => {
    s.put(meta, "meta");
    s.delete(keys("step", step.step));
    s.delete(keys("step", -Infinity, oldest));
    s.put(step, ["step", step.step]);
    s.delete(keys("frame", step.step));
    const kept = new Set(frames);
    const earlier = s.getAllKeys(keys("frame", -Infinity, step.step));
    earlier.onsuccess = () => {
      for (const key of earlier.result as [string, number][]) if (!kept.has(key[1])) s.delete(key);
    };
    s.put(frame, ["frame", step.step]);
  });
}

/** Keeps the tools' settings, changed between steps. */
export function saveMeta(meta: WorkMeta): void {
  write((s) => s.put(meta, "meta"));
}

/** The work is done (delivered): nothing to pick up any more. */
export function clearWork(): void {
  write((s) => s.clear());
}

/** The work kept, if any. */
export async function loadWork(): Promise<Work | null> {
  let meta: WorkMeta | undefined, steps: WorkStep[] = [], frames: Frame[] = [];
  await transact(STORE, "readonly", (s) => {
    const m = s.get("meta"), st = s.getAll(keys("step")), fr = s.getAll(keys("frame"));
    m.onsuccess = () => meta = m.result;
    st.onsuccess = () => steps = st.result;
    fr.onsuccess = () => frames = fr.result;
  });
  return meta && steps.length > 0 && frames.length > 0 ? { ...meta, steps, frames } : null;
}
